import clsx from 'clsx';
import { useMemo } from 'react';
import { Content } from 'src/contexts/ContentContext';
import { useSiteTimezoneContext } from 'src/contexts/SiteTimezoneContext/SiteTimezoneContext';
import { vars } from 'src/core/themes';
import { CheckIcon, IconsFill } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import {
  DateRangePreset,
  DateRangePresetName,
  formatDateRange,
  getDateRangePresets,
  getPresetFromUiDateTimeRange,
} from 'src/utils/dateTimeUtils';
import { DateTimeRange } from 'src/WebApiController';

import * as styles from './DateRangePresets.css';

export type DateRange = {
  from: Date;
  to: Date;
};

export type DateRangePresetsProp = {
  presetNames: DateRangePresetName[];
  selectedDateRange?: DateTimeRange | null;
  onClick: (dateRange: DateRange) => void;
  disabled?: boolean;
  /**
   * If true, the site's timezone will be applied to the date being displayed.
   * the underlying date value (value, data, onDateChange) will not be changed.
   */
  useSiteTimeZone?: boolean;
  hideSuggestionTitle?: boolean;
  showSelected?: boolean;
  variant?: 'default' | 'v2';
};

export function DateRangePresets({
  selectedDateRange,
  presetNames,
  onClick,
  disabled,
  useSiteTimeZone = true,
  hideSuggestionTitle,
  variant = 'default',
}: DateRangePresetsProp) {
  const { toZonedTime, fromZonedTime } = useSiteTimezoneContext();
  const isV2Variant = variant === 'v2';
  const handleClick = (preset: DateRangePreset) => {
    const dateRange = {
      from: useSiteTimeZone
        ? fromZonedTime(preset.range.start)
        : preset.range.start,
      to: useSiteTimeZone ? fromZonedTime(preset.range.end) : preset.range.end,
    };
    onClick(dateRange);
  };

  const presets = useMemo(() => {
    let now = new Date();
    if (useSiteTimeZone) {
      now = toZonedTime(now);
    }
    const presets = getDateRangePresets(presetNames, now);

    return presets;
  }, [useSiteTimeZone, presetNames, toZonedTime]);

  const presetName = getPresetFromUiDateTimeRange(
    selectedDateRange,
    useSiteTimeZone ? toZonedTime : undefined
  )?.name;

  return (
    <div className={styles.suggestionsContainer}>
      {!hideSuggestionTitle && (
        <header className={styles.suggestionsHeading}>
          <Content id={ContentId.DateRangeSuggestionsTitle} />
        </header>
      )}
      {presets.map((preset, index) => {
        const isSelected = presetName === preset.name;
        if (isV2Variant) {
          return (
            <div
              key={index}
              onClick={() => handleClick(preset)}
              className={clsx(styles.suggestionContainerV2, {
                [styles.suggestionV2Selected]: isSelected,
              })}
            >
              <>
                <button className={styles.suggestionV2} disabled={disabled}>
                  <span className={styles.suggestionTextV2}>
                    {preset.label}
                  </span>
                  <span className={styles.suggestionDate}>
                    ({formatDateRange(preset.range)})
                  </span>
                </button>
                {isSelected && (
                  <CheckIcon
                    className={styles.checkIconV2}
                    size={vars.iconSize.s}
                    fill={IconsFill.textSuccess}
                  />
                )}
              </>
            </div>
          );
        }

        return (
          <div
            key={index}
            onClick={() => handleClick(preset)}
            className={styles.suggestionContainer}
          >
            <button
              className={clsx(styles.suggestion, {
                [styles.suggestionSelected]: isSelected,
              })}
              disabled={disabled}
            >
              <span className={styles.suggestionText}>{preset.label}</span>{' '}
              <span>({formatDateRange(preset.range)})</span>
            </button>
          </div>
        );
      })}
    </div>
  );
}
